import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Collapse } from '@mui/material';

/*
 * TFRCard normalises Temporary Flight Restriction status.  It evaluates
 * nearby NOTAMs and reports a standard severity via onUpdate().
 */

const STATUS_MESSAGES = {
  go: '✅ Clear for Takeoff',
  advisory: 'ℹ️ Clear With Advisories',
  caution: '⚠️ Flight Not Recommended',
  'no-go': '❌ Do Not Fly',
  unknown: 'ℹ️ Clear With Advisories'
};

const TFRCard = ({ lat, lon, refreshTrigger, onUpdate }) => {
  const [summaryText, setSummaryText] = useState('TFR data not loaded.');
  const [detailText, setDetailText] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [severity, setSeverity] = useState('unknown');

  const haversineMiles = (lat1, lon1, lat2, lon2) => {
    const toRad = deg => deg * (Math.PI / 180);
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
              Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c * 0.621371;
  };

  useEffect(() => {
    const fetchTFRs = async () => {
      if (!lat || !lon) {
        // Without coordinates we cannot query TFRs.  Report as advisory.
        setSeverity('advisory');
        setSummaryText(STATUS_MESSAGES['advisory']);
        setDetailText('TFR data not available. Click "Use My Location" first.');
        onUpdate && onUpdate('advisory', ['No location set']);
        return;
      }

      try {
        setSummaryText('Loading TFRs…');
        setDetailText('');

        const res = await fetch('https://flightiq.app/API/tfr-proxy.php?t=' + Date.now());
        const data = await res.json();
        const tfrs = data.TFRs || [];
        const lastUpdate = data.updated || null;
        const now = new Date();

        const tfrList = tfrs.map(tfr => {
          const id = tfr.NOTAMID || 'undefined';
          const title = tfr.Activity || 'undefined';
          const eff = new Date(tfr.EffectiveDateTime);
          const exp = new Date(tfr.ExpirationDateTime);
          const radiusMiles = parseFloat(tfr.radius || 0);
          const tfrLat = parseFloat(tfr.geoLat) || 0;
          const tfrLon = parseFloat(tfr.geoLong) || 0;
          const distMiles = haversineMiles(lat, lon, tfrLat, tfrLon);
          return { id, title, eff, exp, radius: radiusMiles, dist: distMiles };
        });

        if (tfrList.length === 0) {
          // No TFRs – safe to fly
          const sev = 'go';
          setSeverity(sev);
          setSummaryText(STATUS_MESSAGES[sev]);
          setDetailText('No Temporary Flight Restrictions are currently published.');
          onUpdate && onUpdate(sev, []);
          return;
        }

        tfrList.sort((a, b) => a.dist - b.dist);
        const closeList = tfrList.slice(0, 3);

        // Determine severity level based on proximity and timing
        let sev = 'go';
        for (const tfr of tfrList) {
          const isNear = tfr.dist <= tfr.radius + 5;
          const timeUntilStart = (tfr.eff - now) / (1000 * 60 * 60);
          const isActive = now >= tfr.eff && now <= tfr.exp;
          const isSoon = timeUntilStart >= 0 && timeUntilStart <= 1;

          if (isNear && (isActive || isSoon)) {
            sev = 'no-go';
            break;
          } else if (isNear && sev !== 'no-go') {
            sev = 'caution';
          }
        }

        // Summary line uses the common status messages
        setSeverity(sev);
        setSummaryText(STATUS_MESSAGES[sev] || STATUS_MESSAGES['unknown']);

        // Detail block with nearest 3
        const html = closeList.map((tfr, idx) => {
          const isInside = tfr.dist <= tfr.radius;
          const isWarn = !isInside && tfr.dist <= tfr.radius + 2;
          const tag = isInside
            ? '<strong style="color:red">INSIDE</strong>'
            : isWarn
            ? '<strong style="color:orange">WITHIN 2&nbsp;MI</strong>'
            : '';
          return `
            <div>
              ${idx + 1}. ${tag} <strong>${tfr.title}</strong><br/>
              ${tfr.radius.toFixed(1)} mi radius – ${tfr.dist.toFixed(1)} mi away<br/>
              <small>${tfr.eff.toLocaleString()} → ${tfr.exp.toLocaleString()} | NOTAM ${tfr.id}</small>
            </div><br/>
          `;
        }).join('');

        const dateStr = lastUpdate
          ? new Date(lastUpdate).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
          : 'N/A';

        setDetailText(`<em>Data updated: ${dateStr}</em><br/><br/>` + html);
        // Provide a reason only if severity is not GO
        let reasons = [];
        if (sev === 'caution') {
          reasons.push('Nearby TFR within advisory distance');
        } else if (sev === 'no-go') {
          reasons.push('Active or imminent TFR within your area');
        }
        if (onUpdate) {
  console.debug('TFRCard: calling onUpdate with', sev, reasons);
  onUpdate(sev, reasons);
}

        console.debug('TFRCard: severity', sev, 'reasons', reasons);
      } catch (err) {
        console.error('TFR fetch error:', err);
        setSeverity('advisory');
        setSummaryText(STATUS_MESSAGES['advisory']);
        setDetailText('Unable to retrieve TFR information at this time.');
        onUpdate && onUpdate('advisory', ['TFR data unavailable']);
      }
    };

    fetchTFRs();
  }, [lat, lon, refreshTrigger]);

  const borderColor = severity === 'no-go' ? 'red'
                    : severity === 'caution' ? 'orange'
                    : severity === 'go' ? 'green'
                    : 'gray';

  return (
    <Card 
      onClick={() => setExpanded(!expanded)}
      sx={{ cursor: 'pointer', borderLeft: `6px solid ${borderColor}`, mb: 1 }}
    >
      <CardContent>
        <Typography variant="h6" sx={{ display: 'flex', justifyContent: 'space-between' }}>
          TFRs <span role="img" aria-label="satellite">🛰️</span>
        </Typography>
        <Typography variant="subtitle1" sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: summaryText }} />
        <Collapse in={expanded}>
          <Typography variant="body2" sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: detailText }} />
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default TFRCard;
